import axios, { AxiosError, AxiosResponse } from "axios";
import { Dispatch, useCallback, useContext, useEffect, useState } from "react";
import { getToken } from "./msal-utils";
import { GetCurrentCompany } from "./customer-context/customer-context";
import { ColumnConfig, SnackBarContext, TableProductsFilterProps } from "@frontend-workspace/react-shared-lib";
import { environment } from '../../../../apps/webkua-app/src/environments/environment';
import { DateRange } from "@mui/x-date-pickers-pro/DateRangePicker";
import { addDays, format } from "date-fns";
import { ITableHookOptions } from "./_models/table-hook-options";
import { RelationProductGroupContext } from "./relation-product-context/relation-product-context";
import { useTranslation } from "react-i18next";

let backend = environment.backend_url;
let courseBackend = environment.course_backend_url;

//backend = "https://webkuaapiv3-staging.azurewebsites.net/api";

if (window.location.hostname === "localhost") {
  backend = "https://localhost:5243/api";
  //backend = "https://webkuaapiv3-dev.azurewebsites.net/api";
  // courseBackend = "https://localhost:5043/api";
}

export async function makeCourseRequest(url: string, type?: string, body?: any): Promise<any> {
  return useMakeBaseRequestHook(courseBackend, url, type, body);
}
export const useMakeRequestHook = () => {
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useCallback(async (url: string, type?: string, body?: any) => {
    const currentToken = await getToken();
    return axios({
      method: (type || 'GET' as any),
      url: `${backend}/${url}`,
      headers: {
        'Authorization': 'Bearer ' + currentToken,
        'content-type': "application/json"
      },
      data: JSON.stringify(body)
    }).then((res: AxiosResponse) => {
      return res.data as any;
    }).catch((error: AxiosError) => {
      const errorMessage = error.response?.status === 403 ? `Kunne ikke gjennomføre request til endpoint: ${url}. Du har ikke rettigheter til å gjøre denne handlingen` : error.message;
      setSnackbarMessage({ content: errorMessage, type: "error" });
      throw error;
    });
  }, [setSnackbarMessage]);
  return makeRequest;
}
// export async function useMakeRequest(url: string, type?: string, body?: any): Promise<any> {
//   return useMakeBaseRequestHook(backend, url, type, body);
// }
async function useMakeBaseRequestHook(backend: string, url: string, type?: string, body?: any): Promise<any> {
  return makeRequest(url, type, body, backend)
    .then(result => {
      return result;
    });
}
export async function makeRequest(url: string, type?: string, body?: any, differentBackend?: string): Promise<any> {
  const backendUrl = differentBackend || backend;
  const currentToken = await getToken();
  return axios({
    method: (type || 'GET' as any),
    url: `${backendUrl}/${url}`,
    headers: {
      'Authorization': 'Bearer ' + currentToken,
      'content-type': "application/json"
    },
    data: JSON.stringify(body)
  }).then((res: AxiosResponse) => {
    return res.data;
  });
}

export async function makeVoiceRequest(url: string, body: any) {
  const currentToken = await getToken();
  return axios({
    method: ('POST' as any),
    url: `${backend}/${url}`,
    headers: {
      'Authorization': 'Bearer ' + currentToken
    },
    data: body
  }).then((res: AxiosResponse) => {
    return res.data;
  });
}
export function useGetRequestNewHook(url: string, initialValue: any, triggers?: Array<any>) {
  return useBaseGetRequestHook(url, initialValue, triggers);
}
export function useCourseGetRequestHook(url: string, initialValue: any, triggers?: Array<any>) {
  return useBaseGetRequestHook(url, initialValue, triggers, courseBackend);
}
function useBaseGetRequestHook(url: string, initialValue: any, triggers?: Array<any>, differentBackend?: string) {
  const backendUrl = differentBackend || backend;
  const [data, setData] = useState<any>(initialValue || null);
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => getData(), triggers || []);
  const getData = () => {
    const urlHasId = (url: string) => !url.includes("undefined") && !url.includes("//");
    if (urlHasId(url)) {
      setIsLoading(true);
      makeRequest(url, 'GET', null, backendUrl).then((result: any) => {
        setIsLoading(false);
        setData(result);
      }).catch(error => {
        console.error(error);
        let errorMessage = `Error fetching data from ${url}`;
        if (error.response && error.response.status) {
          errorMessage += ` (Status Code: ${error.response.status})`;
        }
        if (error.message) {
          errorMessage += ` - ${error.message}`;
        }

        setSnackbarMessage({ content: errorMessage, type: "error" });
      })
    }
  }
  return { data, setData, isLoading, getData };
}

export function UseDateRangeHook(initialStartDate?: Date, initialEndDate?: Date) {
  const [startDate, setStartDate] = useState(initialStartDate || addDays(new Date(), -1));
  const [endDate, setEndDate] = useState(initialEndDate || new Date());

  const updateDates = (dateRange: DateRange<Date> | null) => {
    dateRange && dateRange[0] && setStartDate(dateRange[0]);
    dateRange && dateRange[1] && setEndDate(dateRange[1]);
  }
  return {
    startDate: format(startDate, "yyyy-MM-dd"),
    endDate: format(endDate, "yyyy-MM-dd"),
    onDateChange: updateDates
  }
}

export function useTableConfigHook(tablename: string) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [columns, setColumns] = useState<ColumnConfig[] | undefined>(undefined);
  const [filterModel, setFilterModel] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const getConfig = () => {
    setIsLoading(true);
    makeRequest(`admin/tables/${tablename}`).then(result => {
      setIsLoading(false);
      setColumns(result.columns);
      setFilterModel(result.filterModel);
    })
  };
  useEffect(() => {
    getConfig();
  }, []);
  const { data } = useGetRequestNewHook(`admin/tables/${tablename}`, undefined, []);

  const handleSaveTableConfig = (config: ColumnConfig[] | null, filterModel: any) => config || filterModel ?
    makeRequest(`admin/tables/${tablename}`, "PUT", { columns: config, filterModel }).then(result => {
      setSnackbarMessage({ content: t("requestUtils.configurationSaved"), type: "success" });
      getConfig();
    }) :
    makeRequest(`admin/tables/${tablename}`, "DELETE").then(result => {
      setSnackbarMessage({ content: t("requestUtils.configurationReset"), type: "success" });
      getConfig();
    });
  return {
    columns: columns,
    filterModel: filterModel,
    onSaveTableConfig: handleSaveTableConfig,
    isLoading: isLoading
  }
}

export function UseChartHook(url: string, options?: ITableHookOptions): {
  data: any,
  setData: Dispatch<any>,
  isLoading: boolean,
  getData: () => void,
  startDate: string,
  endDate: string,
  onDateChange: (dateRange: DateRange<Date> | null) => void,
  dateRange: DateRange<Date>,
  forGroup: boolean,
  onForGroupToggle: () => void,
  productFilter: TableProductsFilterProps
} {
  const company = GetCurrentCompany();
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const relationProductGroups = useContext(RelationProductGroupContext);
  const { startDate, endDate, onDateChange } = UseDateRangeHook(options?.startDate, options?.endDate);
  const [selectedProductGroupIds, setSelectedProductGroupIds] = useState<string[]>([]);
  const [forGroup, setForGroup] = useState(false);
  const onForGroupToggle = () => setForGroup(!forGroup);
  const companyNumber = options?.ignoreCompany || !company ? "" : company?.number;
  let fullUrl = options?.supportForGroup && companyNumber ?
    `${url}/${startDate}/${endDate}/${companyNumber}/forGroup/${forGroup}` :
    `${url}/${startDate}/${endDate}/${companyNumber}`;
  if (selectedProductGroupIds.length > 0) {
    fullUrl = fullUrl.concat(`?values=${selectedProductGroupIds.join("&values=")}`);
  }
  const getData = () => {
    setIsLoading(true);
    makeRequest(fullUrl).then(result => {
      setData(result);
      setIsLoading(false);
    });
  }
  const dateRange: DateRange<Date> = [new Date(startDate), new Date(endDate)];
  return {
    data,
    setData,
    isLoading,
    getData,
    startDate,
    endDate,
    onDateChange,
    dateRange,
    forGroup,
    onForGroupToggle,
    productFilter: {
      selectedProductGroupIds,
      setSelectedProductGroupIds,
      products: relationProductGroups
    }
  }
}
export function UseTableHook<X>(url: string, options?: ITableHookOptions): {
  data: Array<X>,
  setData: Dispatch<any>,
  isLoading: boolean,
  getData: () => void,
  startDate: string,
  endDate: string,
  onDateChange: (dateRange: DateRange<Date> | null) => void,
  dateRange: DateRange<Date>,
  forGroup: boolean,
  onForGroupToggle: () => void,
  productFilter: TableProductsFilterProps,
} {
  const company = GetCurrentCompany();
  const relationProductGroups = useContext(RelationProductGroupContext);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<Array<X>>([]);
  const { startDate, endDate, onDateChange } = UseDateRangeHook(options?.startDate, options?.endDate);
  const [selectedProductIds, setSelectedProductGroupIds] = useState<string[]>([]);
  const [forGroup, setForGroup] = useState(false);
  const onForGroupToggle = () => setForGroup(!forGroup);
  const companyNumber = options?.ignoreCompany || !company ? "" : company?.number;

  let fullUrl = options?.supportForGroup && companyNumber ?
    `${url}/${startDate}/${endDate}/${companyNumber}/forGroup/${forGroup}` :
    `${url}/${startDate}/${endDate}/${companyNumber}`;
  if (selectedProductIds.length > 0) {
    fullUrl = fullUrl.concat(`?values=${selectedProductIds.join("&values=")}`);
  }
  const getData = () => {
    setIsLoading(true);
    makeRequest(fullUrl).then(result => {
      setData(result);
      setIsLoading(false);
    });
  }
  const dateRange: DateRange<Date> = [new Date(startDate), new Date(endDate)];
  return {
    data,
    setData,
    isLoading,
    getData,
    startDate,
    endDate,
    onDateChange,
    dateRange,
    forGroup,
    onForGroupToggle,
    productFilter: {
      selectedProductGroupIds: selectedProductIds,
      setSelectedProductGroupIds,
      products: relationProductGroups
    }
  }
}
export function useCourseTableHook<X>(url: string, options?: ITableHookOptions): {
  data: Array<X>,
  setData: Dispatch<any>,
  isLoading: boolean,
  getData: () => void,
  startDate: string,
  endDate: string,
  onDateChange: (dateRange: DateRange<Date> | null) => void,
  dateRange: DateRange<Date>,
  forGroup: boolean,
  onForGroupToggle: () => void,
  productFilter: TableProductsFilterProps
} {
  const company = GetCurrentCompany();
  const relationProductGroups = useContext(RelationProductGroupContext);
  const { startDate, endDate, onDateChange } = UseDateRangeHook(options?.startDate, options?.endDate);
  const [selectedProductGroupIds, setSelectedProductGroupIds] = useState<string[]>([]);
  const [forGroup, setForGroup] = useState(false);
  const onForGroupToggle = () => setForGroup(!forGroup);
  const companyNumber = options?.ignoreCompany || !company ? "" : company?.number;
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0);
  end.setHours(23, 59, 59);
  const unixStartDate = start.getTime() / 1000;
  const unitxEndDate = end.getTime() / 1000;

  let fullUrl = `${url}/${unixStartDate}/${unitxEndDate}`;
  const triggers = [companyNumber, startDate, endDate, fullUrl];
  const { data, setData, isLoading, getData } = useCourseGetRequestHook(fullUrl, [], triggers);
  const dateRange: DateRange<Date> = [new Date(startDate), new Date(endDate)];
  return {
    data,
    setData,
    isLoading,
    getData,
    startDate,
    endDate,
    onDateChange,
    dateRange,
    forGroup,
    onForGroupToggle,
    productFilter: {
      selectedProductGroupIds,
      setSelectedProductGroupIds,
      products: relationProductGroups
    }
  }
}